import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  Button
} from '@mui/material';
import { FaUser, FaBuilding, FaComment, FaExclamationTriangle, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import ClientInfoForm from './ClientInfoForm';
import OrganizationInfoForm from './OrganizationInfoForm';
import FeedbackAndManagementList from './FeedbacksAndComplaintsList';
import IncidentsList from './IncidentsList';

interface TabConfig {
  label: string;
  slug: string;
  icon: JSX.Element;
  component: JSX.Element;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, sm: 2 } }}>{children}</Box>}
    </Box>
  );
};

const allTabsConfig: TabConfig[] = [
  {
    label: 'Feedbacks',
    slug: 'feedbacks',
    icon: <FaComment size={18} />,
    component: <FeedbackAndManagementList />
  },
  {
    label: 'Incidents',
    slug: 'incidents',
    icon: <FaExclamationTriangle size={18} />,
    component: <IncidentsList />
  },
  {
    label: 'Client Info',
    slug: 'client-info',
    icon: <FaUser size={18} />,
    component: <ClientInfoForm />
  },
  {
    label: 'Organization',
    slug: 'organization',
    icon: <FaBuilding size={18} />,
    component: <OrganizationInfoForm />
  }
];

const CorporateServices = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  // Check if coming from documents
  const navigationState = location.state as { isFromDocument?: boolean };
  const isFromDocument = navigationState?.isFromDocument || false;

  // Filter tabs based on navigation source
  const tabsConfig = isFromDocument
    ? allTabsConfig.filter((tab) => tab.slug === 'organization') // Only show Organization
    : allTabsConfig; // Show all tabs

  // Initialize active tab based on URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const tabParam = queryParams.get('tab');
  const initialTab = tabsConfig.findIndex((tab) => tab.slug === tabParam);
  const [activeTab, setActiveTab] = useState(initialTab !== -1 ? initialTab : 0);

  // Update URL when tab changes
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    navigate(`?tab=${tabsConfig[newValue].slug}`, { replace: true });
  };

  // Sync activeTab with URL changes
  useEffect(() => {
    const currentTab = tabsConfig.findIndex((tab) => tab.slug === tabParam);
    if (currentTab !== -1 && currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
  }, [tabParam, activeTab]);

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          textAlign: 'center',
          mb: { xs: 2, md: 3 },
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          position: 'relative',
          pt: { xs: 6, md: 0 } // Extra top padding only on mobile
        }}
      >
        {/* Back Button - Space-efficient positioning */}
        <Box
          sx={{
            position: { xs: 'absolute', md: 'absolute' }, // Keep absolute on all screens
            top: { xs: 16, md: 24 },
            left: { xs: 16, md: 24 },
            zIndex: 1
          }}
        >
          <Button
            onClick={() => navigate(-1)}
            variant="outlined"
            startIcon={<FaArrowLeft />}
            size="small"
            sx={{
              borderColor:
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.23)'
                  : 'rgba(255, 255, 255, 0.23)',
              color: theme.palette.text.primary,
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover
              },
              fontSize: { xs: '0.75rem', md: '0.8125rem' },
              minWidth: 'auto',
              px: 1.5,
              py: 0.5
            }}
          >
            {isMobile ? 'Back' : 'Back'}
          </Button>
        </Box>

        {/* Main Content - Now uses full width */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            width: '100%',
            px: { xs: 4, md: 6 } // Add some side padding
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2, md: 3 },
              flexDirection: { xs: 'column', md: 'row' },
              width: '100%',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 48, md: 64 },
                height: { xs: 48, md: 64 },
                borderRadius: '50%',
                bgcolor: theme.palette.primary.light,
                color: theme.palette.primary.main,
                flexShrink: 0
              }}
            >
              <FaUser size={isMobile ? 24 : 30} />
            </Box>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              component="h1"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                color: theme.palette.text.primary,
                textAlign: 'center',
                maxWidth: 'calc(100% - 100px)' // Prevent text from going under back button
              }}
            >
              Corporate Services
            </Typography>
          </Box>
          <Typography
            variant={isMobile ? 'body2' : 'subtitle1'}
            color="text.secondary"
            sx={{
              maxWidth: { xs: '100%', md: '600px' },
              px: { xs: 0, md: 0 },
              textAlign: 'center'
            }}
          >
            Manage client information, organization details, and service records
          </Typography>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper
        elevation={isMobile ? 0 : 1}
        sx={{
          mb: isMobile ? 1 : 3,
          borderRadius: 2,
          position: 'sticky',
          top: isMobile ? 56 : 64,
          zIndex: 10,
          bgcolor: 'background.paper'
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered={!isMobile}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              minHeight: '48px',
              px: isMobile ? 1.5 : 3,
              '&.Mui-selected': {
                color: theme.palette.primary.main
              }
            }
          }}
        >
          {tabsConfig.map((tab, index) => (
            <Tab
              key={index}
              label={isMobile ? tab.label.split(' ')[0] : tab.label}
              icon={tab.icon}
              iconPosition="start"
              sx={{
                minWidth: 'unset',
                '& .MuiTab-iconWrapper': {
                  mr: isMobile ? 0.5 : 1
                }
              }}
            />
          ))}
        </Tabs>
        <Divider />
      </Paper>

      {/* Tab Content */}
      <Grid container spacing={isMobile ? 1 : 3}>
        <Grid item xs={12}>
          <Card
            elevation={isMobile ? 0 : 1}
            sx={{
              borderRadius: 3,
              border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
              overflow: 'visible'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: isMobile ? 2 : 3,
                  pb: isMobile ? 1 : 2,
                  bgcolor: 'background.default',
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}
              >
                {tabsConfig[activeTab].icon}
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  sx={{
                    fontWeight: 600,
                    ml: 1.5,
                    color: theme.palette.text.primary
                  }}
                >
                  {tabsConfig[activeTab].label}
                </Typography>
              </Box>
              {tabsConfig.map((tab, index) => (
                <TabPanel key={index} value={activeTab} index={index}>
                  {tab.component}
                </TabPanel>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CorporateServices;
